/**
 * Zustand des DSFA-Reports für die Plaketten an den Einstellungen.
 *
 * Der Server bewertet (`services/dsfaReport.ts`); hier wird nur einmal geladen
 * und bei jeder Änderung neu geholt. Als Auslöser dient das Ereignis
 * `ff:mutated`, das der API-Client nach jedem erfolgreichen schreibenden
 * Aufruf sendet: So färbt sich eine Plakette um, sobald die Einstellung neben
 * ihr gespeichert ist – ohne dass jede Seite davon wissen muss.
 */

import { useEffect, useState } from 'react';
import { api } from './api';

export type DsfaStatus = 'green' | 'yellow' | 'red';

export interface DsfaItem {
  key: string;
  group: string;
  label: string;
  why: string;
  status: DsfaStatus;
  reason: string;
  value: boolean | number | string | null;
  display: string;
  where: { label: string; to: string };
}

export interface DsfaReport {
  items: DsfaItem[];
  summary: Record<DsfaStatus, number>;
  generatedAt: string;
}

export const DSFA_LABEL: Record<DsfaStatus, string> = { green: 'grün', yellow: 'gelb', red: 'rot' };
export const DSFA_VARIANT: Record<DsfaStatus, string> = { green: 'success', yellow: 'warning', red: 'danger' };

let report: DsfaReport | null = null;
let failed = false;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | undefined;

export async function loadDsfa(): Promise<DsfaReport | null> {
  try {
    report = await api.get<DsfaReport>('/api/dsfa/report');
    failed = false;
  } catch {
    // Fehlendes Recht oder Netzfehler: Plaketten bleiben einfach aus.
    report = null;
    failed = true;
  }
  listeners.forEach((listener) => listener());
  return report;
}

if (typeof window !== 'undefined') {
  window.addEventListener('ff:mutated', () => {
    if (listeners.size === 0) return;
    clearTimeout(timer);
    timer = setTimeout(() => void loadDsfa(), 250);
  });
}

export function useDsfa(): { report: DsfaReport | null; failed: boolean; reload: () => Promise<DsfaReport | null> } {
  const [, force] = useState(0);
  useEffect(() => {
    const listener = () => force((n) => n + 1);
    listeners.add(listener);
    if (!report && !failed) void loadDsfa();
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return { report, failed, reload: loadDsfa };
}
